import { useState, useEffect, useRef } from "react";
import { Link } from "@/lib/router-compat";
import { Menu, X, ChevronDown, ChevronRight, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  esTreatmentEntries,
  esPracticeEntries,
  esResourcesEntries,
  esOtherLinks,
  esNavLabels,
  isGroup,
  type NavEntry,
} from "@/components/esNavData";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const SAINT_PHONE = "954-827-9793";

/**
 * Spanish-language navbar. Same brand styling as the English Navbar, but with
 * Spanish labels and links pointing to /es/* where a translation exists
 * (falling back to the English route otherwise, so nothing 404s).
 */
export default function EsNavbar({
  hideSpravato = false,
}: { hideSpravato?: boolean } = {}) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [treatmentsOpen, setTreatmentsOpen] = useState(false);
  const [practiceOpen, setPracticeOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [ketamineSubOpen, setKetamineSubOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setTreatmentsOpen(false);
        setKetamineSubOpen(false);
        setPracticeOpen(false);
        setResourcesOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const close = () => {
    setOpen(false);
    setTreatmentsOpen(false);
    setPracticeOpen(false);
    setResourcesOpen(false);
    setKetamineSubOpen(false);
  };

  const renderEntry = (entry: NavEntry, idx: number) => {
    if (isGroup(entry)) {
      return (
        <div key={entry.label}>
          <button
            onClick={() => setKetamineSubOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground/85 hover:bg-muted"
          >
            {entry.label}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${ketamineSubOpen ? "rotate-180" : ""}`}
            />
          </button>
          {ketamineSubOpen && (
            <div className="ml-3 flex flex-col gap-0.5 border-l border-border pl-3">
              {entry.children.map((l) =>
                l.isRoute ? (
                  <Link
                    key={l.href}
                    to={l.href}
                    onClick={close}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground/80 hover:bg-muted"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={close}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground/80 hover:bg-muted"
                  >
                    {l.label}
                  </a>
                ),
              )}
            </div>
          )}
        </div>
      );
    }
    const featured = "featured" in entry && entry.featured;
    return (
      <Link
        key={entry.href ?? idx}
        to={entry.href}
        onClick={close}
        className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm ${featured ? "font-extrabold text-accent" : "font-medium text-foreground/80"} hover:bg-muted`}
      >
        {featured && <span className="h-1.5 w-1.5 rounded-full bg-accent" />}
        {entry.label}
      </Link>
    );
  };

  return (
    <>
      {/* Announcement Bar */}
      <div className="bg-primary text-white">
        <div className="container-px mx-auto flex max-w-7xl items-center justify-center gap-2 px-3 py-2 text-xs sm:gap-3 sm:py-2.5 sm:text-sm">
          <span className="hidden h-2 w-2 shrink-0 rounded-full bg-accent sm:inline-flex" />
          <Link
            to="/es/saint"
            className="text-white/90 font-semibold text-center leading-tight hover:text-accent transition-colors"
          >
            {esNavLabels.announcement}
          </Link>
          <a
            href="https://www.valant.io/myio/NeuroWellness"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden shrink-0 text-white/70 transition-colors hover:text-accent sm:inline-block sm:text-xs lg:absolute lg:right-4"
          >
            {esNavLabels.patientPortal}
          </a>
        </div>
      </div>

      <header
        className={`fixed inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-background/95 backdrop-blur-md shadow-sm border-b border-border" : "bg-background/80 backdrop-blur-sm"}`}
        style={{
          paddingTop: "env(safe-area-inset-top)",
          top: "var(--announcement-height, 0px)",
        }}
      >
        <nav className="container-px mx-auto flex h-12 items-center justify-between px-4 sm:h-14">
          <Link to="/es" className="flex items-center gap-2.5">
            <img
              src="https://vibe.filesafe.space/1792357746068722743/assets/77c54df1-224e-472f-8384-515b6968349f.webp"
              srcSet="https://vibe.filesafe.space/1792357746068722743/assets/77c54df1-224e-472f-8384-515b6968349f.webp 1x, https://vibe.filesafe.space/1792357746068722743/assets/0288c30c-02b5-4ac2-a94a-68665598d6da.webp 2x"
              alt="Neuro Wellness TMS Centers of America"
              className="h-9 w-auto sm:h-10"
              width={163}
              height={40}
              loading="eager"
              decoding="async"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden items-center gap-6 lg:flex" ref={dropdownRef}>
            <Link
              to="/es/saint"
              className="inline-flex items-center rounded-full bg-gradient-accent px-4 py-1.5 text-sm font-extrabold uppercase tracking-wide leading-none text-accent-foreground shadow-glow ring-1 ring-accent/30 transition-all hover:opacity-90"
            >
              SAINT®
            </Link>

            <div
              className="relative"
              onMouseEnter={() => setTreatmentsOpen(true)}
              onMouseLeave={() => setTreatmentsOpen(false)}
            >
              <button className="flex items-center gap-1 text-sm font-semibold text-foreground/85 transition-colors hover:text-primary">
                {esNavLabels.treatments}
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${treatmentsOpen ? "rotate-180" : ""}`}
                />
              </button>
              {treatmentsOpen && (
                <div className="absolute left-0 top-full pt-3">
                  <div className="w-64 rounded-xl border border-border bg-card shadow-premium py-2">
                    {esTreatmentEntries
                      .filter(
                        (e) =>
                          !hideSpravato ||
                          isGroup(e) ||
                          !("href" in e) ||
                          e.href !== "/es/spravato",
                      )
                      .map((e, i) => (
                        <div key={i}>{renderEntry(e, i)}</div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            <div
              className="relative"
              onMouseEnter={() => setPracticeOpen(true)}
              onMouseLeave={() => setPracticeOpen(false)}
            >
              <button className="flex items-center gap-1 text-sm font-semibold text-foreground/85 transition-colors hover:text-primary">
                {esNavLabels.ourPractice}
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${practiceOpen ? "rotate-180" : ""}`}
                />
              </button>
              {practiceOpen && (
                <div className="absolute left-0 top-full pt-3">
                  <div className="w-60 rounded-xl border border-border bg-card shadow-premium py-2">
                    {esPracticeEntries.flatMap((l) =>
                      isGroup(l)
                        ? [
                            <div
                              key={`grp-${l.label}`}
                              className="px-4 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground"
                            >
                              {l.label}
                            </div>,
                            ...l.children.map((c) => (
                              <Link
                                key={c.href}
                                to={c.href}
                                onClick={close}
                                className="block px-4 py-2.5 text-sm font-medium text-foreground/85 hover:bg-muted hover:text-primary"
                              >
                                {c.label}
                              </Link>
                            )),
                          ]
                        : [
                            <Link
                              key={l.href}
                              to={l.href}
                              onClick={close}
                              className="block px-4 py-2.5 text-sm font-medium text-foreground/85 hover:bg-muted hover:text-primary"
                            >
                              {l.label}
                            </Link>,
                          ],
                    )}
                  </div>
                </div>
              )}
            </div>

            <div
              className="relative"
              onMouseEnter={() => setResourcesOpen(true)}
              onMouseLeave={() => setResourcesOpen(false)}
            >
              <button className="flex items-center gap-1 text-sm font-semibold text-foreground/85 transition-colors hover:text-primary">
                {esNavLabels.resources}
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${resourcesOpen ? "rotate-180" : ""}`}
                />
              </button>
              {resourcesOpen && (
                <div className="absolute left-0 top-full pt-3">
                  <div className="w-60 rounded-xl border border-border bg-card shadow-premium py-2">
                    {esResourcesEntries.map((l) => (
                      <Link
                        key={l.href}
                        to={l.href}
                        onClick={close}
                        className="block px-4 py-2.5 text-sm font-medium text-foreground/85 hover:bg-muted hover:text-primary"
                      >
                        {l.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {esOtherLinks.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className="text-sm font-semibold text-foreground/85 hover:text-primary"
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="hidden items-center gap-3 lg:flex">
            <LanguageSwitcher variant="light" />
            <a
              href="tel:9548279793"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-accent px-5 py-2.5 text-sm font-bold text-accent-foreground shadow-glow hover:opacity-90 transition-opacity"
            >
              <PhoneCall className="h-4 w-4" />
              {esNavLabels.callNumber}
            </a>
          </div>

          {/* Mobile */}
          <div className="flex items-center gap-1 lg:hidden">
            <LanguageSwitcher variant="light" />
            <a
              href="tel:9548279793"
              className="flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-bold text-accent"
              aria-label="Llamar a Neuro Wellness"
            >
              <PhoneCall className="h-4 w-4" />
              <span className="hidden xs:inline">{esNavLabels.call}</span>
            </a>
            <button
              className="text-primary p-2"
              onClick={() => setOpen((v) => !v)}
              aria-label="Abrir menú"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>

        {open && (
          <div
            className="lg:hidden border-t border-border bg-background shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="container-px mx-auto flex max-w-7xl flex-col gap-1 py-4">
              <Link
                to="/es/saint"
                onClick={close}
                className="flex items-center justify-between rounded-lg px-3 py-3 text-base font-extrabold uppercase tracking-wide text-accent hover:bg-muted"
              >
                SAINT®
                <ChevronRight className="h-5 w-5" />
              </Link>

              <button
                onClick={() => setTreatmentsOpen((v) => !v)}
                className="flex items-center justify-between rounded-lg px-3 py-3 text-base font-semibold text-foreground/85 hover:bg-muted"
              >
                {esNavLabels.treatments}
                <ChevronDown
                  className={`h-5 w-5 transition-transform ${treatmentsOpen ? "rotate-180" : ""}`}
                />
              </button>
              {treatmentsOpen && (
                <div className="ml-3 flex flex-col gap-0.5 border-l border-border pl-3">
                  {esTreatmentEntries
                    .filter(
                      (e) =>
                        !hideSpravato ||
                        isGroup(e) ||
                        !("href" in e) ||
                        e.href !== "/es/spravato",
                    )
                    .map((e, i) => renderEntry(e, i))}
                </div>
              )}

              <button
                onClick={() => setPracticeOpen((v) => !v)}
                className="flex items-center justify-between rounded-lg px-3 py-3 text-base font-semibold text-foreground/85 hover:bg-muted"
              >
                {esNavLabels.ourPractice}
                <ChevronDown
                  className={`h-5 w-5 transition-transform ${practiceOpen ? "rotate-180" : ""}`}
                />
              </button>
              {practiceOpen && (
                <div className="ml-3 flex flex-col gap-0.5 border-l border-border pl-3">
                  {esPracticeEntries.flatMap((l) =>
                    isGroup(l)
                      ? [
                          <div
                            key={`grp-${l.label}`}
                            className="px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground"
                          >
                            {l.label}
                          </div>,
                          ...l.children.map((c) => (
                            <Link
                              key={c.href}
                              to={c.href}
                              onClick={close}
                              className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground/80 hover:bg-muted"
                            >
                              {c.label}
                            </Link>
                          )),
                        ]
                      : [
                          <Link
                            key={l.href}
                            to={l.href}
                            onClick={close}
                            className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground/80 hover:bg-muted"
                          >
                            {l.label}
                          </Link>,
                        ],
                  )}
                </div>
              )}

              <button
                onClick={() => setResourcesOpen((v) => !v)}
                className="flex items-center justify-between rounded-lg px-3 py-3 text-base font-semibold text-foreground/85 hover:bg-muted"
              >
                {esNavLabels.resources}
                <ChevronDown
                  className={`h-5 w-5 transition-transform ${resourcesOpen ? "rotate-180" : ""}`}
                />
              </button>
              {resourcesOpen && (
                <div className="ml-3 flex flex-col gap-0.5 border-l border-border pl-3">
                  {esResourcesEntries.map((l) => (
                    <Link
                      key={l.href}
                      to={l.href}
                      onClick={close}
                      className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground/80 hover:bg-muted"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              )}

              {esOtherLinks.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={close}
                  className="rounded-lg px-3 py-3 text-base font-semibold text-foreground/85 hover:bg-muted"
                >
                  {l.label}
                </Link>
              ))}

              <Button
                asChild
                className="mt-2 bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a
                  href="tel:9548279793"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2"
                >
                  <PhoneCall className="h-4 w-4" />
                  Llamar {SAINT_PHONE}
                </a>
              </Button>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
